"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import LogoAnimatedGreeks from "@/components/logos/LogoAnimatedGreeks";
import GreekParticleBackground from "@/components/GreekParticleBackground";
import { logUsage } from "@/lib/logger";
import { clearAuthCache } from "@/lib/auth-cache";

const IconMail = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);
const IconLockKeyhole = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="16" r="1" /><rect x="3" y="10" width="18" height="12" rx="2" /><path d="M7 10V7a5 5 0 0 1 10 0v3" />
  </svg>
);
const IconGauge = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" />
  </svg>
);
const IconArrowRight = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="5" x2="19" y1="12" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const IconAlertTriangle = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" x2="12" y1="9" y2="13" /><line x1="12" x2="12.01" y1="17" y2="17" />
  </svg>
);

export default function LoginPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCleaning, setIsCleaning] = useState(true);
  const [expiredNotice, setExpiredNotice] = useState(false);
  const [notFoundNotice, setNotFoundNotice] = useState(false);
  // A still-valid share link redirected here (e.g. outside market hours)
  const [linkClosedNotice, setLinkClosedNotice] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // True once we give up waiting for Turnstile to render (script/iframe blocked client-side)
  const [turnstileBlocked, setTurnstileBlocked] = useState(false);
  // Ref for the Turnstile widget container
  const turnstileRef = useRef<HTMLDivElement>(null);

  const SITE_KEY = "0x4AAAAAACX1GLpF5caEfTOC";
  const TURNSTILE_LOAD_TIMEOUT_MS = 8000;

  // Handle Turnstile script and widget
  useEffect(() => {
    setTurnstileBlocked(false);

    // Add Turnstile script to head if not present
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      // Ad-blockers / privacy extensions commonly block this script entirely
      script.onerror = () => setTurnstileBlocked(true);
      document.head.appendChild(script);
    }

    // Define global callback for Turnstile
    (window as any).onTurnstileSuccess = (token: string) => {
      setTurnstileToken(token);
    };

    // Render the widget only once
    const renderTurnstile = () => {
      if (typeof window !== 'undefined' && (window as any).turnstile && turnstileRef.current) {
        // Remove any existing widgets inside the container
        while (turnstileRef.current.firstChild) {
          turnstileRef.current.removeChild(turnstileRef.current.firstChild);
        }
        (window as any).turnstile.render(turnstileRef.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => {
            setTurnstileBlocked(false);
            setTurnstileToken(token);
          },
          'error-callback': () => setTurnstileBlocked(true),
          theme: 'dark',
        });
      }
    };

    // Wait for the script to load, then render
    let interval: NodeJS.Timeout | null = null;
    const checkAndRender = () => {
      if ((window as any).turnstile && turnstileRef.current) {
        renderTurnstile();
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
      }
    };
    interval = setInterval(checkAndRender, 300);

    // If the widget never renders (script blocked, iframe blocked by ad-blocker/shields),
    // stop waiting and tell the user instead of leaving a dead box on screen.
    const timeout: NodeJS.Timeout = setTimeout(() => {
      if (!(window as any).turnstile) setTurnstileBlocked(true);
    }, TURNSTILE_LOAD_TIMEOUT_MS);

    return () => {
      // Clean up interval and widget
      if (interval) clearInterval(interval);
      clearTimeout(timeout);
      if (turnstileRef.current) {
        while (turnstileRef.current.firstChild) {
          turnstileRef.current.removeChild(turnstileRef.current.firstChild);
        }
      }
    };
  }, []);

  // Force a clean start when the login page is visited
  useEffect(() => {
    const performCleanup = async () => {
      try {
        console.log('🧹 [Login] Initializing clean start...');

        // Check if we are already authenticated via API
        const authResponse = await fetch('/api/auth/authorization');
        if (authResponse.ok) {
          const currentAuth = await authResponse.json();
          // If active session is a guest/share-token session, purge it immediately for a clean start
          if (currentAuth?.user?.isShareToken) {
            console.log('🧹 [Login] Guest share token session detected, purging for clean login');
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
          } else if (currentAuth && currentAuth.isAuthenticated) {
            console.log('ℹ️ [Login] Active real user session found, skipping automatic cleanup');
            setIsCleaning(false);
            return;
          }
        } else {
          // Perform logout to destroy any existing server session
          await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
        }

        // Clear browser storage (prevents stale feature flags or legacy tokens)
        if (typeof window !== 'undefined') {
          const prefKey = 'whale_tape_preferences';
          const prefs = localStorage.getItem(prefKey);

          localStorage.clear();
          sessionStorage.clear();

          // Restore user preferences
          if (prefs) localStorage.setItem(prefKey, prefs);

          console.log('✅ [Login] Clean start complete');
        }
      } catch (err) {
        console.warn('⚠️ [Login] Cleanup encountered an issue:', err);
      } finally {
        setIsCleaning(false);
      }
    };

    performCleanup();

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('expired') === '1') {
        setExpiredNotice(true);
      }
      if (params.get('not_found') === '1') {
        setNotFoundNotice(true);
      }
      // Share links (#key=…) that land here via a redirect (/dashboard/… without a
      // session, or the market-hours gate) carry the key in the fragment — look it
      // up so the visitor sees why instead of a bare login form.
      const hashKey = window.location.hash.match(/key=([^&]+)/)?.[1];
      if (hashKey) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        fetch(`/api/share-token/status?token=${encodeURIComponent(decodeURIComponent(hashKey))}`)
          .then((r) => r.json())
          .then((d) => {
            if (d?.status === 'expired' || d?.status === 'revoked') setExpiredNotice(true);
            else if (d?.status === 'not_found') setNotFoundNotice(true);
            else if (d?.status === 'active') setLinkClosedNotice(true);
          })
          .catch(() => { /* leave the plain login form */ });
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setShowResendVerification(false);
    setLoading(true);

    try {
      // Validate Turnstile
      if (!turnstileToken) {
        setError("Please complete the human verification.");
        setLoading(false);
        return;
      }

      // Call login API directly
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          turnstileToken: turnstileToken
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        // Handle error - only redirect if email is specifically unverified, NOT on human verification failures
        if (result.code === 'EMAIL_NOT_VERIFIED' || result.error?.toLowerCase() === 'email not verified') {
          router.push(`/unverified?email=${encodeURIComponent(formData.email)}`);
          return;
        }
        throw new Error(result.error || 'Login failed');
      }

      console.log('✅ Login successful, session created');
      // Clear authorization cache to force fresh data fetch
      clearAuthCache(); // Legacy support

      // CRITICAL: Force React Query to drop stale auth data immediately
      // This ensures useAuth() fetches fresh permissions/trials on the Dashboard
      await queryClient.invalidateQueries({ queryKey: ['auth'] });
      await queryClient.resetQueries({ queryKey: ['auth'] });

      // Store API token for legacy/API compatibility
      if (typeof window !== 'undefined' && result.user?.api_token) {
        localStorage.setItem('auth_token', result.user.api_token);
      }

      // Wait a tiny bit for cookie persistence (browser safety)
      await new Promise(resolve => setTimeout(resolve, 100));

      // Log usage
      logUsage('login', { email: formData.email });

      // Redirect to dashboard
      const urlParams = new URLSearchParams(window.location.search);
      const redirect = urlParams.get('redirect') || '/dashboard';
      router.push(redirect);
    } catch (err: any) {
      let errorMessage = "Something went wrong.";

      // Handle specific error cases
      if (err?.message?.includes('Invalid email or password')) {
        console.log("Authentication failed: Invalid credentials");
        errorMessage = "Invalid email or password. Please check your credentials.";
      } else if (err?.message?.includes('Verification failed') || err?.message?.includes('human')) {
        console.warn("Turnstile verification failed");
        errorMessage = err?.message || "Verification failed. Please try again.";
      } else if (err?.message?.includes('Network error') || err?.message?.includes('fetch')) {
        console.error("Network error - Cannot connect to server");
        errorMessage = "Cannot connect to server. Please check your internet connection.";
      } else {
        console.error("Unexpected login error:", err);
        errorMessage = err?.message || "Something went wrong.";
      }

      setError(errorMessage);
      setLoading(false);

      // Reset Turnstile on error
      if (typeof (window as any).turnstile !== 'undefined') {
        try {
          (window as any).turnstile.reset();
        } catch (e) {
          console.error('Turnstile reset failed:', e);
        }
        setTurnstileToken(null);
      }
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError("Please enter your email address.");
      return;
    }

    setResendingEmail(true);
    setError("");

    try {
      const { authApi } = await import('@/lib/api-client');
      const result = await authApi.requestVerification(formData.email);

      if (result.success) {
        setError("");
        alert("Verification email sent! Please check your inbox.");
        setShowResendVerification(false);
      } else {
        setError(result.error || "Failed to send verification email. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to send verification email. Please try again.");
    } finally {
      setResendingEmail(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="relative min-h-screen bg-brand-navy flex items-center justify-center p-4 md:p-8 overflow-hidden">
      <GreekParticleBackground />
      <div className="relative z-10 w-full max-w-5xl overflow-hidden rounded-2xl border border-white/10 bg-black/20 shadow-2xl shadow-black/40 lg:grid lg:grid-cols-2">

        {/* ── Left: Form Panel ─────────────────────────────────────────── */}
        <div className="flex flex-col justify-center px-6 py-10 sm:px-10 md:px-12 md:py-14">
          <Link href="/" className="mb-8 inline-flex items-center gap-3">
            <LogoAnimatedGreeks className="w-9 h-9" interval={3000} />
            <span className="font-montserrat text-lg font-bold text-white">
              ΘDTE <span className="text-brand-orange">Toolbox</span>
            </span>
          </Link>

          <div className="mb-8">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-blue">
              Your Workspace Awaits
            </span>
            <h1 className="mt-2 font-montserrat text-3xl font-bold tracking-tight text-white">
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              {isCleaning
                ? "Preparing secure session..."
                : "Sign in to continue to your account."}
            </p>
          </div>

          {error && (
            <div className={`mb-6 rounded-lg px-4 py-3 text-sm ${error.includes('verification') || error.includes('human')
                ? 'bg-amber-500/10 border border-amber-500/50 text-amber-400'
                : 'bg-red-500/10 border border-red-500/50 text-red-400'
              }`}>
              <div className="flex items-start gap-2">
                {(error.includes('verification') || error.includes('human')) && (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mt-0.5 shrink-0">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                )}
                <div>
                  <p className="font-semibold mb-1">
                    {error.includes('verification') || error.includes('human')
                      ? 'Security Verification'
                      : 'Authentication Error'}
                  </p>
                  {error}
                </div>
              </div>

              {showResendVerification && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                  className="mt-3 w-full px-4 py-3 bg-brand-blue hover:bg-brand-blue/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-wide rounded-lg transition-all shadow-md hover:shadow-lg"
                >
                  {resendingEmail
                    ? "Sending..."
                    : "Resend Verification Email"}
                </button>
              )}
            </div>
          )}

          {expiredNotice && (
            <div className="mb-6 rounded-lg bg-amber-500/10 border border-amber-500/40 p-4 text-sm text-amber-200 flex items-start gap-2.5">
              <IconAlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-300">Access Link Expired</p>
                <p className="text-xs text-amber-200/80 mt-0.5">
                  Your access link has expired. Sign in to your account, or request a new link from the ΘDTE Toolbox team or the person who shared it.
                </p>
              </div>
            </div>
          )}

          {linkClosedNotice && (
            <div className="mb-6 rounded-lg bg-cyan-500/10 border border-cyan-500/40 p-4 text-sm text-cyan-100 flex items-start gap-2.5">
              <IconAlertTriangle className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-cyan-300">Market Closed</p>
                <p className="text-xs text-cyan-100/80 mt-0.5">
                  Your shared link is still valid, but this tool is only available on market days from 7:00 AM to 5:00 PM ET. Open the same link again during those hours.
                </p>
              </div>
            </div>
          )}

          {notFoundNotice && (
            <div className="mb-6 rounded-lg bg-rose-500/10 border border-rose-500/40 p-4 text-sm text-rose-200 flex items-start gap-2.5">
              <IconAlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-rose-300">Access Code Does Not Exist</p>
                <p className="text-xs text-rose-200/80 mt-0.5">
                  The access code in your link does not exist or is invalid. Sign in with your account or request a new link from the sender.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                Email address
              </label>
              <div className="relative">
                <IconMail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-gray-700 bg-white/5 py-3 pl-10 pr-4 text-white placeholder-gray-500 transition-colors focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-semibold text-white">
                  Password
                </label>
                <Link href="/forgot-password" className="text-xs text-brand-blue hover:text-brand-blue/80 transition-colors">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <IconLockKeyhole className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-gray-700 bg-white/5 py-3 pl-10 pr-11 text-white placeholder-gray-500 transition-colors focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <label className="flex items-center text-sm text-gray-400">
              <input
                type="checkbox"
                className="mr-2 w-4 h-4 text-brand-blue bg-white/5 border-gray-600 rounded focus:ring-brand-blue"
              />
              Remember me
            </label>

            {/* Cloudflare Turnstile - only one instance rendered via ref */}
            <div className="flex justify-center min-h-[65px]">
              <div ref={turnstileRef} className={turnstileBlocked ? 'hidden' : undefined} />
              {turnstileBlocked && (
                <div className="w-full px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/50 text-amber-400 text-sm text-center">
                  <p className="font-semibold mb-1">Sign-in check couldn't load</p>
                  <p>
                    Your browser's ad-blocker or privacy shield is blocking{' '}
                    <code className="text-xs">challenges.cloudflare.com</code> — the same fast,
                    lightweight network we use to keep the whole app quick. Add{' '}
                    <code className="text-xs">0dte-toolbox.com</code> to your blocker's allowlist
                    and reload this page to sign in.
                  </p>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !turnstileToken}
              className={`w-full rounded-lg border py-3.5 font-bold shadow-lg transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:hover:translate-y-0 enabled:border-transparent enabled:bg-brand-orange enabled:text-white enabled:shadow-brand-orange/20 enabled:hover:bg-brand-orange/90 enabled:hover:shadow-brand-orange/40 ${
                formData.email && formData.password
                  ? "border-brand-orange/40 bg-white/10 text-gray-200 shadow-brand-orange/20"
                  : "border-white/10 bg-white/10 text-gray-300 shadow-black/20"
              }`}
            >
              {loading
                ? "Signing in..."
                : (!turnstileToken
                  ? "Verify you are human"
                  : "Sign in")}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-400">
            Don't have an account?{" "}
            <Link href="/register" className="font-semibold text-brand-orange transition-colors hover:text-brand-orange/80">
              Sign up
            </Link>
          </p>
        </div>

        {/* ── Right: Brand Panel ───────────────────────────────────────── */}
        <div className="relative hidden overflow-hidden bg-gradient-to-br from-brand-blue via-brand-navy to-brand-orange/80 lg:flex lg:flex-col lg:justify-center lg:px-12 lg:py-14">
          {/* Ambient glow accents */}
          <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-brand-orange/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-brand-blue/40 blur-3xl" />

          <div className="relative z-10">
            <div className="mb-8 flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm">
                <IconGauge className="h-7 w-7 text-white" />
              </div>
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm">
                <span className="font-montserrat text-2xl font-extrabold text-white">Θ</span>
              </div>
            </div>

            <span className="text-xs font-bold uppercase tracking-widest text-white/70">
              Make It Yours
            </span>
            <h2 className="mt-2 font-montserrat text-3xl font-bold tracking-tight text-white">
              Your edge in 0DTE
            </h2>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/80">
              Real-time Greeks, GEX/DEX exposure, and institutional flow — sign in and pick up right where you left off.
            </p>

            <Link
              href="/register?source=trial"
              className="mt-8 inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur-sm transition-all hover:bg-white/20"
            >
              Create account <IconArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-3 font-mono text-[11px] text-white/60">
              Free to start · No card needed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
